import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import OtterIcon from "@/components/app/OtterIcon";
import {
  Sparkles, RefreshCw, ExternalLink, Check, Trash2, Clock, Hash, Loader2,
} from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const m = Math.floor(Math.max(0, Date.now() - then) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Admin-only dashboard widget: lists Arlo's pending weekly auto-drafts and
 * gives one-click approve / discard / force-run controls.
 *
 * Renders NOTHING for non-admins. Renders NOTHING when there are no pending
 * drafts AND the cron has never run — keeps the dashboard quiet by default.
 */
export default function AutoBlogQueue({ user }) {
  const isAdmin = user && typeof user === "object" && user.role === "admin";
  const [state, setState] = useState(null); // {pending, count, last_run_at, last_outcome}
  const [busy, setBusy] = useState(null); // draft_id currently being acted on, or "__run__"
  const [error, setError] = useState("");

  const refresh = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/auto-blog/pending");
      setState(data);
    } catch (e) {
      setError(formatApiError(e));
      setState({ pending: [], count: 0, last_run_at: null, last_outcome: null });
    }
  };

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  if (!isAdmin) return null;

  // Hide entirely when there's truly nothing to show (no pending + cron never run).
  if (state === null) return null;
  const hasPending = state.pending && state.pending.length > 0;
  const neverRun = !state.last_run_at;
  if (!hasPending && neverRun) {
    // Show a tiny "Run now" cue so admins know the feature exists, but keep it slim.
    return (
      <div
        className="mb-8 border border-dashed border-foreground/30 bg-white p-5 flex items-center gap-4"
        data-testid="auto-blog-queue-empty-firstrun"
      >
        <div className="w-9 h-9 bg-foreground grid place-items-center text-background flex-shrink-0">
          <OtterIcon size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
            // weekly auto-blog · admin only
          </div>
          <div className="text-sm mt-1">
            Arlo can draft you a fresh post every Monday — picks your best opportunity keyword, writes
            the whole thing, queues it here for review.
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-none"
          onClick={runNow}
          disabled={busy === "__run__"}
          data-testid="auto-blog-run-now-empty"
        >
          {busy === "__run__" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
          Draft one now
        </Button>
      </div>
    );
  }

  async function runNow() {
    setBusy("__run__");
    try {
      const { data } = await api.post("/admin/auto-blog/run");
      if (data.reason === "drafted") {
        toast.success(`Arlo drafted "${data.title}" — review below.`);
      } else if (data.reason === "rate_limited") {
        toast.message("Already ran recently", { description: "Auto-blog respects a 5-day floor between runs." });
      } else if (data.reason === "no_topic") {
        toast.message("No opportunity keyword right now", { description: "Track a few high-volume / low-difficulty keywords first." });
      } else if (data.reason === "ai_failed") {
        toast.error("Arlo couldn't write that one — check the backend logs.");
      } else {
        toast.message("Auto-blog tick complete");
      }
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  }

  async function approve(draft) {
    setBusy(draft.id);
    try {
      const { data } = await api.post(`/admin/auto-blog/${draft.id}/approve`);
      toast.success("Published to /blog", {
        description: data.url ? `Live at ${data.url}` : undefined,
      });
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  }

  async function discard(draft) {
    setBusy(draft.id);
    try {
      await api.post(`/admin/auto-blog/${draft.id}/discard`);
      toast.message("Draft discarded", { description: `Arlo won't re-pick "${draft.target_keyword}" next week.` });
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mb-8 border border-foreground bg-white" data-testid="auto-blog-queue">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 bg-foreground grid place-items-center text-background flex-shrink-0">
            <OtterIcon size={20} />
          </div>
          <div className="min-w-0">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              // arlo&apos;s weekly drafts · admin only
            </div>
            <div className="text-sm font-medium mt-0.5">
              {hasPending
                ? `${state.count} draft${state.count === 1 ? "" : "s"} pending review`
                : "Inbox zero — Arlo's caught up"}
              {state.last_run_at && (
                <span className="ml-2 text-muted-foreground">· last run {timeAgo(state.last_run_at)}</span>
              )}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-none"
          onClick={runNow}
          disabled={busy === "__run__"}
          data-testid="auto-blog-run-now"
        >
          {busy === "__run__" ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          )}
          Run now
        </Button>
      </div>

      {/* Errors */}
      {error && (
        <div className="px-4 py-3 text-xs text-red-700 bg-red-50 border-b border-red-200" data-testid="auto-blog-error">
          {error}
        </div>
      )}

      {/* List */}
      {hasPending ? (
        <ul className="divide-y divide-border" data-testid="auto-blog-pending-list">
          {state.pending.map((d) => (
            <li key={d.id} className="p-4" data-testid={`auto-blog-pending-${d.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> auto-generated
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {timeAgo(d.auto_generated_at)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Hash className="w-3 h-3" /> {d.word_count} words · {d.reading_minutes} min
                    </span>
                  </div>
                  <h3 className="font-semibold tracking-tight mt-2 text-base leading-snug truncate">
                    {d.title || "Untitled draft"}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="border border-border bg-secondary/50 px-1.5 py-0.5 font-mono-data text-[10px] uppercase tracking-widest">
                      kw: {d.target_keyword}
                    </span>
                    {d.opportunity?.volume != null && (
                      <span>vol {Number(d.opportunity.volume).toLocaleString()}/mo</span>
                    )}
                    {d.opportunity?.difficulty != null && (
                      <span>· difficulty {d.opportunity.difficulty}/100</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="rounded-none"
                    data-testid={`auto-blog-review-${d.id}`}
                  >
                    <Link to={`/app/projects/${d.project_id}?draft=${d.id}`}>
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Review
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    className="rounded-none bg-foreground hover:bg-foreground/90"
                    onClick={() => approve(d)}
                    disabled={busy === d.id}
                    data-testid={`auto-blog-approve-${d.id}`}
                  >
                    {busy === d.id ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Approve &amp; publish
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-none text-muted-foreground hover:text-red-600"
                    onClick={() => discard(d)}
                    disabled={busy === d.id}
                    data-testid={`auto-blog-discard-${d.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="p-6 text-sm text-muted-foreground" data-testid="auto-blog-empty">
          Nothing pending. Arlo will draft another post on his next weekly run — or hit{" "}
          <span className="font-medium text-foreground">Run now</span> to generate one immediately.
        </div>
      )}
    </div>
  );
}
